@if (legacyCreateMode) {
  <section class="legacy-create-shell">
    <header class="legacy-create-header">
      <div>
        <span>DPP Publisher</span>
        <h2>{{'contentTypes.wizard.createTemplate' | translate}}</h2>
        <p>{{'contentTypes.wizard.legacyIntro' | translate}}</p>
      </div>
      <button type="button" mat-icon-button (click)="cancelNewContentType()" [disabled]="creating" [attr.aria-label]="'contentTypes.wizard.close' | translate">
        <mat-icon>close</mat-icon>
      </button>
    </header>
    <mat-dialog-content class="legacy-create-content">
      <form [formGroup]="newContentForm" class="legacy-create-form">
        <mat-form-field appearance="outline">
          <mat-label>{{'contentTypes.name' | translate}}</mat-label>
          <input type="text" matInput formControlName="displayName" placeholder="{{'contentTypes.name' | translate}}" data-nar="content-type-create-name">
          @if (newContentForm.get('displayName')?.hasError('required')) {
            <mat-error>{{'taskTemplates.mandatory' | translate}}</mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label data-nar="content-type-create-language">{{'defaultLanguage' | translate}}</mat-label>
          <mat-select formControlName="language">
            @for (lang of languagesList | keyvalue; track lang) {
              <mat-option [value]="lang.key" data-nar="content-type-create-language-option">{{lang.value}}</mat-option>
            }
          </mat-select>
          @if (newContentForm.get('language')?.hasError('required')) {
            <mat-error>{{'taskTemplates.mandatory' | translate}}</mat-error>
          }
        </mat-form-field>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end" class="legacy-create-actions">
      <button type="button" mat-button style="color:var(--warmOrange);" (click)="cancelNewContentType()" [disabled]="creating">{{'cancel' | translate}}</button>
      <button type="button" mat-button class="orange-button" (click)="saveNewContentType(false)" [disabled]="creating || newContentForm.invalid">
        <mat-icon>add_circle</mat-icon>
        {{'contentTypes.create' | translate}}
      </button>
    </mat-dialog-actions>
  </section>
} @else {
  <section class="dpp-setup-shell">
    <header class="wizard-header">
      <div>
        <span class="wizard-kicker">{{'contentTypes.wizard.assistant' | translate}}</span>
        <h1>{{'contentTypes.wizard.newTemplate' | translate}}</h1>
        <p>{{'contentTypes.wizard.intro' | translate}}</p>
      </div>
      <button type="button" mat-icon-button class="close-button" (click)="cancelNewContentType()" [disabled]="creating" [attr.aria-label]="'contentTypes.wizard.close' | translate">
        <mat-icon>close</mat-icon>
      </button>
    </header>
    <nav class="wizard-steps" [attr.aria-label]="'contentTypes.wizard.steps' | translate">
      <button type="button" [class.active]="currentStep === 1" [class.complete]="currentStep > 1" (click)="goToStep(1)">
        <span>1</span>
        <strong>{{'contentTypes.basicData' | translate}}</strong>
        <small>{{'contentTypes.wizard.nameAndLanguage' | translate}}</small>
      </button>
      <button type="button" [class.active]="currentStep === 2" [class.complete]="currentStep > 2" [disabled]="currentStep < 2" (click)="goToStep(2)">
        <span>2</span>
        <strong>{{'contentTypes.wizard.chooseStructure' | translate}}</strong>
        <small>{{'contentTypes.wizard.presetOrCustom' | translate}}</small>
      </button>
      <button type="button" [class.active]="currentStep === 3" [class.complete]="currentStep > 3" [disabled]="currentStep < 3" (click)="goToStep(3)">
        <span>3</span>
        <strong>{{'contentTypes.editSchema' | translate}}</strong>
        <small>{{'contentTypes.wizard.reviewAndEdit' | translate}}</small>
      </button>
      <button type="button" [class.active]="currentStep === 4" [disabled]="currentStep < 4" (click)="goToStep(4)">
        <span>4</span>
        <strong>{{'contentTypes.wizard.reviewAndFinish' | translate}}</strong>
        <small>{{'contentTypes.wizard.createOrOpenDesign' | translate}}</small>
      </button>
    </nav>
    <mat-dialog-content class="wizard-content">
      @if (currentStep === 1) {
        <section class="wizard-panel">
          <div class="panel-copy">
            <span class="wizard-kicker">{{'contentTypes.wizard.step' | translate:{ number: 1 } }}</span>
            <h2>{{'contentTypes.wizard.nameTemplate' | translate}}</h2>
            <p>{{'contentTypes.wizard.nameTemplateHint' | translate}}</p>
          </div>
          <form [formGroup]="newContentForm" class="basics-form">
            <mat-form-field appearance="outline">
              <mat-label>{{'contentTypes.name' | translate}}</mat-label>
              <input type="text" matInput formControlName="displayName" [placeholder]="'contentTypes.wizard.nameExample' | translate" data-nar="dpp-name">
              <mat-hint>{{'contentTypes.wizard.nameHint' | translate}}</mat-hint>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label data-nar="dpp-language">{{'defaultLanguage' | translate}}</mat-label>
              <mat-select formControlName="language">
                @for (lang of languagesList | keyvalue; track lang) {
                  <mat-option [value]="lang.key" data-nar="dpp-language-dropdown-option">{{lang.value}}</mat-option>
                }
              </mat-select>
              <mat-hint>{{'contentTypes.wizard.languagesHint' | translate}}</mat-hint>
            </mat-form-field>
          </form>
          <div class="setup-note">
            <mat-icon>auto_awesome</mat-icon>
            <p>{{'contentTypes.wizard.setupNote' | translate}}</p>
          </div>
        </section>
      }
      @if (currentStep === 2) {
        <section class="wizard-panel">
          <div class="section-heading">
            <div>
              <span class="wizard-kicker">{{'contentTypes.wizard.step' | translate:{ number: 2 } }}</span>
              <h2>{{'contentTypes.wizard.selectStructure' | translate}}</h2>
              <p>{{'contentTypes.wizard.selectStructureHint' | translate}}</p>
            </div>
            <span class="field-count">{{'contentTypes.wizard.fieldCount' | translate:{ count: selectedSchemaFieldCount } }}</span>
          </div>
          <div class="schema-disclaimer my-4">
            <mat-icon>info</mat-icon>
            <p>{{'contentTypes.wizard.presetsComingSoonHint' | translate}}</p>
          </div>
          <div class="preset-grid">
            @for (preset of unavailableSchemaPresets; track preset.id) {
              <button
                type="button"
                class="preset-card unavailable"
                disabled>
                <span class="preset-icon"><mat-icon>{{preset.icon}}</mat-icon></span>
                <span class="preset-content">
                  <span class="preset-head">
                    <span>
                      <span class="preset-meta">{{'contentTypes.wizard.comingSoon' | translate}}</span>
                      <strong>{{preset.title}}</strong>
                    </span>
                    <mat-icon class="preset-check">lock</mat-icon>
                  </span>
                  <span class="preset-description">{{preset.descriptionKey | translate}}</span>
                </span>
              </button>
            }
            <button
              type="button"
              class="preset-card custom"
              [class.selected]="isCustomSchemaSelected"
              (click)="startCustomSchema()">
              <span class="preset-icon"><mat-icon>edit_note</mat-icon></span>
              <span class="preset-content">
                <span class="preset-head">
                  <span>
                    <span class="preset-meta">{{'contentTypes.wizard.customStructure' | translate}}</span>
                    <strong>{{'contentTypes.wizard.defineYourself' | translate}}</strong>
                  </span>
                  <mat-icon class="preset-check">{{isCustomSchemaSelected ? 'check_circle' : 'radio_button_unchecked'}}</mat-icon>
                </span>
                <span class="preset-description">{{'contentTypes.wizard.customStructureHint' | translate}}</span>
              </span>
            </button>
          </div>
        </section>
      }
      @if (currentStep === 3) {
        <section class="wizard-panel schema-review-panel">
          <div class="section-heading">
            <div>
              <span class="wizard-kicker">{{'contentTypes.wizard.step' | translate:{ number: 3 } }}</span>
              <h2>{{'contentTypes.wizard.reviewStructure' | translate}}</h2>
              <p>{{'contentTypes.wizard.reviewStructureHint' | translate}}</p>
            </div>
            <span class="field-count">{{'contentTypes.wizard.fieldCount' | translate:{ count: selectedSchemaFieldCount } }}</span>
          </div>
          <app-content-types-schema-view-dialog
            [embedded]="true"
            [embeddedContentType]="embeddedSchemaContentType"
            (schemaApplied)="applySchemaFromEditor($event)">
          </app-content-types-schema-view-dialog>
        </section>
      }
      @if (currentStep === 4) {
        <section class="wizard-panel finish-panel">
          <div class="finish-hero">
            <span class="finish-icon"><mat-icon>task_alt</mat-icon></span>
            <div>
              <span class="wizard-kicker">{{'contentTypes.wizard.step' | translate:{ number: 4 } }}</span>
              <h2>{{'contentTypes.wizard.reviewAndFinish' | translate}}</h2>
              <p>{{'contentTypes.wizard.finishHint' | translate}}</p>
            </div>
          </div>
          <div class="finish-review">
            <div>
              <mat-icon>article</mat-icon>
              <span><strong>{{newContentForm.value.displayName || '-'}}</strong><small>{{'contentTypes.wizard.templateName' | translate}}</small></span>
            </div>
            <div>
              <mat-icon>language</mat-icon>
              <span><strong>{{newContentForm.value.language || '-'}}</strong><small>{{'defaultLanguage' | translate}}</small></span>
            </div>
            <div>
              <mat-icon>account_tree</mat-icon>
              <span><strong>{{'contentTypes.wizard.fieldCount' | translate:{ count: selectedSchemaFieldCount } }}</strong><small>{{'contentTypes.schema' | translate}}</small></span>
            </div>
          </div>
          <section class="finish-fields">
            <div class="finish-fields-header">
              <h3>{{'contentTypes.wizard.reviewStructureShort' | translate}}</h3>
              <span>{{'contentTypes.wizard.fieldCount' | translate:{ count: selectedSchemaFieldCount } }}</span>
            </div>
            <div class="finish-field-list">
              @for (field of flattenedSchemaFields; track field.path) {
                <div class="finish-field-row" [style.padding-left.px]="getFieldIndent(field)">
                  <mat-icon>{{field.type === 'object' ? 'account_tree' : field.type === 'array' ? 'format_list_bulleted' : field.type === 'number' ? 'tag' : field.type === 'boolean' ? 'toggle_on' : field.type === 'date' ? 'calendar_month' : 'text_fields'}}</mat-icon>
                  <strong>{{field.key}}</strong>
                  <span>{{('contentTypes.wizard.fieldTypes.' + field.type) | translate}}</span>
                  @if (field.mandatory) {
                    <small>{{'contentTypes.schemaRequiredField' | translate}}</small>
                  }
                </div>
              }
            </div>
          </section>
          <div class="finish-choice">
            <mat-icon>info</mat-icon>
            <div>
              <h3>{{'contentTypes.wizard.designOptional' | translate}}</h3>
              <p>{{'contentTypes.wizard.designOptionalHint' | translate}}</p>
            </div>
          </div>
        </section>
      }
    </mat-dialog-content>
    <footer class="wizard-footer">
      <button type="button" mat-button class="secondary-gray-button" (click)="currentStep = currentStep - 1" [disabled]="currentStep === 1 || creating">
        {{'tridys.importWizard.back' | translate}}
      </button>
      <div class="footer-actions">
        <button type="button" mat-button style="color:var(--warmOrange);" (click)="cancelNewContentType()" [disabled]="creating">{{'cancel' | translate}}</button>
        @if (currentStep === 1) {
          <button type="button" mat-button class="orange-button" (click)="nextFromBasics()">{{'tridys.importWizard.next' | translate}}</button>
        }
        @if (currentStep === 2) {
          <button type="button" mat-button class="orange-button" (click)="currentStep = 3">{{'tridys.importWizard.next' | translate}}</button>
        }
        @if (currentStep === 3) {
          <button type="button" mat-button class="orange-button" (click)="currentStep = 4">{{'tridys.importWizard.next' | translate}}</button>
        }
        @if (currentStep === 4) {
          <button type="button" mat-button class="secondary-gray-button" [disabled]="creating" (click)="saveNewContentType(false)">
            {{'contentTypes.wizard.createTemplateAction' | translate}}
          </button>
        }
        @if (currentStep === 4) {
          <button type="button" mat-button class="orange-button finish-primary-button" [disabled]="creating" (click)="saveNewContentType(true)">
            <mat-icon>book_online</mat-icon>
            {{'contentTypes.wizard.createAndOpenDesign' | translate}}
          </button>
        }
      </div>
    </footer>
  </section>
}
